import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Navbar } from '@/components/layout/Navbar';

import { useNavigate } from 'react-router-dom';
import { uploadService } from '@/services/upload';
import { auth } from '@/config/firebase';
import { createUserWithEmailAndPassword, sendEmailVerification } from 'firebase/auth';


export function RegisterInvestorPage() {
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(false);
    const [focusIndustry, setFocusIndustry] = useState<string[]>([]);
    const [investmentStage, setInvestmentStage] = useState<string[]>([]);
    const [isIndividual, setIsIndividual] = useState(false);

    // Form state
    const [fullName, setFullName] = useState('');
    const [email, setEmail] = useState('');

    const [password, setPassword] = useState(''); // Added password field temporarily or assume separate step
    const [orgName, setOrgName] = useState('');
    const [currency, setCurrency] = useState('INR');
    const [minCheckSize, setMinCheckSize] = useState('');
    const [maxCheckSize, setMaxCheckSize] = useState('');
    const [preferredGeo, setPreferredGeo] = useState('');
    const [linkedIn, setLinkedIn] = useState('');
    const [title, setTitle] = useState('');

    // File state
    const [avatarFile, setAvatarFile] = useState<File | null>(null);
    const [orgLogoFile, setOrgLogoFile] = useState<File | null>(null);

    // Previews
    const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
    const [orgLogoPreview, setOrgLogoPreview] = useState<string | null>(null);

    // File refs
    const avatarInputRef = useRef<HTMLInputElement>(null);
    const orgLogoInputRef = useRef<HTMLInputElement>(null);

    const toggleIndustry = (item: string) => {
        if (item === 'All') {
            setFocusIndustry(['All']);
        } else {
            if (focusIndustry.includes('All')) {
                setFocusIndustry([item]);
            } else {
                if (focusIndustry.includes(item)) {
                    setFocusIndustry(focusIndustry.filter(i => i !== item));
                } else {
                    setFocusIndustry([...focusIndustry, item]);
                }
            }
        }
    };

    const toggleStage = (item: string) => {
        if (investmentStage.includes(item)) {
            setInvestmentStage(investmentStage.filter(i => i !== item));
        } else {
            setInvestmentStage([...investmentStage, item]);
        }
    };

    const handleFileClick = (ref: React.RefObject<HTMLInputElement>) => {
        ref.current?.click();
    };

    const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            setAvatarFile(file);
            const objectUrl = URL.createObjectURL(file);
            setAvatarPreview(objectUrl);
        }
    };

    const removeAvatar = (e: React.MouseEvent) => {
        e.stopPropagation();
        setAvatarPreview(null);
        if (avatarInputRef.current) {
            avatarInputRef.current.value = '';
        }
    };

    const handleOrgLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            setOrgLogoFile(file);
            const objectUrl = URL.createObjectURL(file);
            setOrgLogoPreview(objectUrl);
        }
    };

    const removeOrgLogo = (e: React.MouseEvent) => {
        e.stopPropagation();
        setOrgLogoPreview(null);
        if (orgLogoInputRef.current) {
            orgLogoInputRef.current.value = '';
        }
    };

    const formatNumber = (value: string) => {
        // Remove existing commas and non-numeric characters
        const cleanValue = value.replace(/[^0-9]/g, '');
        if (!cleanValue) return '';
        // Add commas
        return new Intl.NumberFormat('en-IN').format(Number(cleanValue));
    };

    const handleCheckSizeChange = (e: React.ChangeEvent<HTMLInputElement>, setter: (val: string) => void) => {
        const value = e.target.value;
        const cleanValue = value.replace(/,/g, '');
        if (/^\d*$/.test(cleanValue)) {
            setter(formatNumber(cleanValue));
        }
    };

    return (
        <div className="bg-unicorn-mesh-light dark:bg-none dark:bg-background-dark text-slate-900 dark:text-white overflow-x-hidden antialiased transition-colors duration-300 min-h-screen relative flex flex-col font-sans">
            <style>{`
                .material-symbols-outlined { font-variation-settings: 'FILL' 0, 'wght' 400, 'GRAD' 0, 'opsz' 24; }
                .filled-icon { font-variation-settings: 'FILL' 1, 'wght' 400, 'GRAD' 0, 'opsz' 24; }
                ::-webkit-scrollbar { width: 8px; }
                ::-webkit-scrollbar-track { background: transparent; }
                ::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 4px; }
                ::-webkit-scrollbar-thumb:hover { background: #94a3b8; }
                .dark ::-webkit-scrollbar-thumb { background: #334155; }
                .dark ::-webkit-scrollbar-thumb:hover { background: #475569; }
                .glass-card {
                    background: rgba(255, 255, 255, 0.7);
                    backdrop-filter: blur(10px);
                    -webkit-backdrop-filter: blur(10px);
                    border: 1px solid rgba(255, 255, 255, 0.5);
                }
                .dark .glass-card {
                    background: rgba(15, 23, 42, 0.6);
                    border: 1px solid rgba(255, 255, 255, 0.05);
                }
                .logo-gradient-text {
                    background: linear-gradient(to right, #3B52D0, #4194F2, #A378CF, #F0766E);
                    -webkit-background-clip: text;
                    -webkit-text-fill-color: transparent;
                }
            `}</style>

            <div className="fixed inset-0 z-[-1] opacity-0 dark:opacity-40 bg-unicorn-mesh pointer-events-none transition-opacity duration-500"></div>

            <Navbar variant="minimal" />

            <main className="flex-1 w-full max-w-[1920px] mx-auto px-4 md:px-8 py-4 md:py-6 relative z-10">
                <div className="mb-6 text-center relative py-6 px-6 rounded-3xl glass-card overflow-hidden shadow-lg">
                    <div className="absolute inset-0 bg-logo-gradient opacity-5"></div>
                    <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-[#4f46e5]/20 to-transparent"></div>
                    <h1 className="relative z-10 bg-logo-gradient bg-clip-text text-transparent text-4xl md:text-5xl font-black leading-tight tracking-[-0.033em] mb-0 pb-2 drop-shadow-sm font-display">Investor Registration</h1>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
                    {/* User Profile Section (Left Column) */}
                    <section className="lg:col-span-4 flex flex-col gap-6">
                        <div className="rounded-3xl overflow-hidden glass-card shadow-lg border border-white/50 dark:border-slate-700">
                            <div className="bg-logo-gradient p-5 flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <span className="material-symbols-outlined text-white text-3xl">person</span>
                                    <h2 className="text-white text-xl font-extrabold leading-tight font-display">User Profile</h2>
                                </div>
                            </div>

                            <div className="p-6 flex flex-col gap-6">
                                {/* Avatar */}
                                <div className="flex justify-center">
                                    <div
                                        className="group relative flex flex-col items-center gap-2 cursor-pointer"
                                        onClick={() => handleFileClick(avatarInputRef)}
                                    >
                                        <div className="w-32 h-32 rounded-full bg-white dark:bg-slate-800 border-4 border-dashed border-indigo-200 dark:border-slate-600 flex items-center justify-center group-hover:border-[#4f46e5] transition-colors relative shadow-sm">
                                            {avatarPreview ? (
                                                <>
                                                    <img src={avatarPreview} alt="Avatar Preview" className="w-full h-full object-cover rounded-full" />
                                                    <button
                                                        onClick={removeAvatar}
                                                        className="absolute -top-1 -right-1 p-1.5 bg-white dark:bg-slate-800 text-slate-500 hover:text-red-500 rounded-full shadow-lg z-50 border border-slate-100 dark:border-slate-700"
                                                    >
                                                        <span className="material-symbols-outlined text-sm font-bold">close</span>
                                                    </button>
                                                </>
                                            ) : (
                                                <>
                                                    <div className="absolute inset-0 bg-indigo-50/50 dark:bg-slate-700/50 rounded-full z-0"></div>
                                                    <span className="material-symbols-outlined text-indigo-300 dark:text-slate-500 text-3xl z-10">add_a_photo</span>
                                                </>
                                            )}
                                            <input
                                                ref={avatarInputRef}
                                                accept="image/*"
                                                className="hidden"
                                                type="file"
                                                onChange={handleAvatarChange}
                                            />
                                        </div>
                                        {!avatarPreview && <span className="text-xs font-bold text-[#4f46e5]">Upload Photo</span>}
                                    </div>
                                </div>

                                {/* Identity */}
                                <div className="flex flex-col gap-4">
                                    <h3 className="text-[#4f46e5] dark:text-[#818cf8] font-bold text-xs uppercase tracking-wider opacity-90 border-b border-indigo-100 dark:border-slate-700 pb-2">Identity</h3>
                                    <label className="block">
                                        <span className="text-slate-700 dark:text-slate-300 text-sm font-bold leading-normal mb-2 block">Full Name</span>
                                        <input
                                            value={fullName}
                                            onChange={(e) => setFullName(e.target.value)}
                                            className="w-full h-11 rounded-lg border border-slate-200 dark:border-slate-600 bg-white/50 dark:bg-slate-900/50 dark:text-white px-4 text-base focus:border-[#4f46e5] focus:ring-1 focus:ring-[#4f46e5] outline-none transition-all placeholder:text-slate-400 dark:placeholder:text-slate-600 shadow-sm"
                                            type="text"
                                        />
                                    </label>
                                    <label className="block">
                                        <span className="text-slate-700 dark:text-slate-300 text-sm font-bold leading-normal mb-2 block">Email ID</span>
                                        <input
                                            value={email}
                                            onChange={(e) => setEmail(e.target.value)}
                                            className="w-full h-11 rounded-lg border border-slate-200 dark:border-slate-600 bg-white/50 dark:bg-slate-900/50 dark:text-white px-4 text-base focus:border-[#4f46e5] focus:ring-1 focus:ring-[#4f46e5] outline-none transition-all placeholder:text-slate-400 dark:placeholder:text-slate-600 shadow-sm" type="email"
                                        />

                                    </label>
                                    <label className="block">
                                        <span className="text-slate-700 dark:text-slate-300 text-sm font-bold leading-normal mb-2 block">Password</span>
                                        <input
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value)}
                                            className="w-full h-11 rounded-lg border border-slate-200 dark:border-slate-600 bg-white/50 dark:bg-slate-900/50 dark:text-white px-4 text-base focus:border-[#4f46e5] focus:ring-1 focus:ring-[#4f46e5] outline-none transition-all placeholder:text-slate-400 dark:placeholder:text-slate-600 shadow-sm" type="password"
                                            placeholder="Choose a password"
                                        />
                                    </label>
                                </div>

                                {/* Professional */}
                                <div className="flex flex-col gap-4">
                                    <h3 className="text-[#4f46e5] dark:text-[#818cf8] font-bold text-xs uppercase tracking-wider opacity-90 border-b border-indigo-100 dark:border-slate-700 pb-2">Professional</h3>
                                    <label className="block">
                                        <span className="text-slate-700 dark:text-slate-300 text-sm font-bold leading-normal mb-2 block">Professional Title</span>
                                        <input
                                            value={title}
                                            onChange={(e) => setTitle(e.target.value)}
                                            className="w-full h-11 rounded-lg border border-slate-200 dark:border-slate-600 bg-white/50 dark:bg-slate-900/50 dark:text-white px-4 text-base focus:border-[#4f46e5] focus:ring-1 focus:ring-[#4f46e5] outline-none transition-all placeholder:text-slate-400 dark:placeholder:text-slate-600 shadow-sm" type="text"
                                        />
                                    </label>
                                    <label className="block">
                                        <div className="flex justify-between items-center mb-2">
                                            <span className="text-slate-700 dark:text-slate-300 text-sm font-bold leading-normal">LinkedIn URL</span>
                                            <span className="text-slate-400 text-xs font-semibold italic">(Optional)</span>
                                        </div>
                                        <div className="relative">
                                            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                                <span className="text-[#0a66c2] text-lg font-bold">in/</span>
                                            </div>
                                            <input
                                                value={linkedIn}
                                                onChange={(e) => setLinkedIn(e.target.value)}
                                                className="w-full h-11 rounded-lg border border-slate-200 dark:border-slate-600 bg-white/50 dark:bg-slate-900/50 dark:text-white pl-12 pr-4 text-base focus:border-[#4f46e5] focus:ring-1 focus:ring-[#4f46e5] outline-none transition-all placeholder:text-slate-400 dark:placeholder:text-slate-600 shadow-sm" type="text"
                                            />
                                        </div>
                                    </label>
                                </div>
                            </div>
                        </div>
                    </section>

                    {/* Org & Preferences Section (Right Column) */}
                    <section className="lg:col-span-8 flex flex-col gap-8">

                        {/* Organization Details */}
                        <div className="rounded-3xl overflow-hidden glass-card shadow-lg border border-white/50 dark:border-slate-700">
                            <div className="bg-logo-gradient p-5 flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <span className="material-symbols-outlined text-white text-3xl">corporate_fare</span>
                                    <h2 className="text-white text-xl font-extrabold leading-tight font-display">Organization Details</h2>
                                </div>
                            </div>
                            <div className="p-6 md:p-8 flex flex-col gap-8">

                                {/* Individual Investor Checkbox */}
                                <div
                                    className={`flex items-center gap-4 p-4 rounded-xl border cursor-pointer transition-all w-full group
                                        ${isIndividual
                                            ? 'bg-indigo-50 border-[#4f46e5] dark:bg-slate-800'
                                            : 'border-slate-200 dark:border-slate-600 bg-white/50 dark:bg-slate-900/50 hover:border-indigo-300'
                                        }`}
                                    onClick={() => setIsIndividual(!isIndividual)}
                                >
                                    <div className={`
                                        flex items-center justify-center w-6 h-6 rounded border transition-colors
                                        ${isIndividual ? 'bg-[#4f46e5] border-[#4f46e5]' : 'bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-500'}
                                    `}>
                                        {isIndividual && <span className="material-symbols-outlined text-white text-sm font-bold">check</span>}
                                    </div>
                                    <span className="text-slate-700 dark:text-slate-200 font-bold text-sm select-none">
                                        Register as an individual</span>
                                </div>

                                {!isIndividual && (
                                    <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-start animate-fade-in-up">
                                        <div className="md:col-span-2 flex flex-col">
                                            <label className="block text-slate-700 dark:text-slate-300 text-sm font-bold leading-normal mb-2">Org Logo</label>
                                            <div
                                                className="w-32 h-32 rounded-2xl border-2 border-dashed border-slate-300 dark:border-slate-600 bg-slate-50/50 dark:bg-slate-800/50 hover:border-[#4f46e5] hover:bg-indigo-50/50 dark:hover:bg-indigo-900/10 transition-all cursor-pointer flex flex-col items-center justify-center gap-2 text-center p-2 group relative overflow-hidden"
                                                onClick={() => handleFileClick(orgLogoInputRef)}
                                            >
                                                {orgLogoPreview ? (
                                                    <>
                                                        <img src={orgLogoPreview} alt="Org Logo Preview" className="w-full h-full object-contain" />
                                                        <button
                                                            onClick={removeOrgLogo}
                                                            className="absolute top-1 right-1 p-1 bg-white dark:bg-slate-800 text-slate-500 hover:text-red-500 rounded-full shadow-lg z-20 border border-slate-100 dark:border-slate-700"
                                                        >
                                                            <span className="material-symbols-outlined text-xs font-bold">close</span>
                                                        </button>
                                                    </>
                                                ) : (
                                                    <>
                                                        <span className="material-symbols-outlined text-slate-400 text-4xl group-hover:text-[#4f46e5] transition-colors">cloud_upload</span>
                                                        <span className="text-xs font-semibold text-slate-500">Drag or click</span>
                                                    </>
                                                )}
                                                <input ref={orgLogoInputRef} accept="image/*" className="hidden" type="file" onChange={handleOrgLogoChange} />
                                            </div>
                                        </div>
                                        <div className="md:col-span-10 flex flex-col gap-6">
                                            <label className="block">
                                                <span className="text-slate-700 dark:text-slate-300 text-sm font-bold leading-normal mb-2 block">Organization Name</span>
                                                <input
                                                    value={orgName}
                                                    onChange={(e) => setOrgName(e.target.value)}
                                                    className="w-full h-11 rounded-lg border border-slate-200 dark:border-slate-600 bg-white/50 dark:bg-slate-900/50 dark:text-white px-4 text-base focus:border-[#4f46e5] focus:ring-1 focus:ring-[#4f46e5] outline-none transition-colors placeholder:text-slate-400"
                                                    type="text"
                                                />
                                            </label>
                                            <label className="block">
                                                <span className="text-slate-700 dark:text-slate-300 text-sm font-bold leading-normal mb-2 block">Establishment Year</span>
                                                <div className="relative">
                                                    <input className="w-full h-11 rounded-lg border border-slate-200 dark:border-slate-600 bg-white/50 dark:bg-slate-900/50 dark:text-white px-4 text-base focus:border-[#4f46e5] focus:ring-1 focus:ring-[#4f46e5] outline-none transition-colors placeholder:text-slate-400" max="2099" min="1900" placeholder="YYYY" type="number" />
                                                    <div className="absolute inset-y-0 right-3 flex items-center pointer-events-none">
                                                        <span className="material-symbols-outlined text-slate-400 text-sm">calendar_today</span>
                                                    </div>
                                                </div>
                                            </label>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Preferences */}
                        <div className="rounded-3xl overflow-hidden glass-card shadow-lg border border-white/50 dark:border-slate-700">
                            <div className="bg-logo-gradient p-5 flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <span className="material-symbols-outlined text-white text-3xl">tune</span>
                                    <h2 className="text-white text-xl font-extrabold leading-tight font-display">Preferences</h2>
                                </div>
                            </div>
                            <div className="p-6 md:p-8">
                                <div className="mb-8">
                                    <span className="text-slate-700 dark:text-slate-300 text-sm font-bold leading-normal mb-3 block">
                                        Focus Industry <span className="text-[#ec4899] font-normal ml-1">(Select at least 1)</span>
                                    </span>
                                    <div className="flex flex-wrap gap-2">
                                        {['All', 'AI/ML', 'SaaS', 'Fintech', 'HealthTech', 'Web3', 'CleanTech', 'EdTech', 'Robotics', 'AgriTech'].map((tech) => (
                                            <button
                                                key={tech}
                                                onClick={() => toggleIndustry(tech)}
                                                className={`px-4 py-2 rounded-full text-sm font-bold transition-all shadow-sm flex items-center gap-1 ${focusIndustry.includes(tech)
                                                    ? 'bg-gradient-to-r from-[#ec4899] to-[#f43f5e] text-white shadow-pink-500/20'
                                                    : 'bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:bg-pink-50 dark:hover:bg-slate-700 hover:text-pink-600'
                                                    }`}
                                            >
                                                {tech}
                                                {focusIndustry.includes(tech) && tech !== 'All' && <span className="material-symbols-outlined text-[16px]">close</span>}
                                            </button>
                                        ))}
                                        <button className="px-4 py-2 rounded-full border-2 border-dashed border-slate-300 dark:border-slate-600 text-slate-500 dark:text-slate-400 text-sm font-bold hover:text-[#ec4899] hover:border-[#ec4899] transition-colors flex items-center gap-1">
                                            <span className="material-symbols-outlined text-[18px]">add</span> Add
                                        </button>
                                    </div>
                                </div>

                                <div className="mb-8">
                                    <span className="text-slate-700 dark:text-slate-300 text-sm font-bold leading-normal mb-3 block">
                                        Investment Stage <span className="text-[#ec4899] font-normal ml-1">(Select at least 1)</span>
                                    </span>
                                    <div className="flex flex-wrap gap-2">
                                        {['All', 'Angel', 'Pre-seed', 'Seed', 'Series A', 'Series B', 'Series C'].map((stage) => (
                                            <button
                                                key={stage}
                                                onClick={() => toggleStage(stage)}
                                                className={`px-4 py-2 rounded-full text-sm font-bold transition-all shadow-sm flex items-center gap-1 ${investmentStage.includes(stage)
                                                    ? 'bg-gradient-to-r from-[#ec4899] to-[#f43f5e] text-white shadow-pink-500/20'
                                                    : 'bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:bg-pink-50 dark:hover:bg-slate-700 hover:text-pink-600'
                                                    }`}
                                            >
                                                {stage}
                                                {investmentStage.includes(stage) && stage !== 'All' && <span className="material-symbols-outlined text-[16px]">close</span>}
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                    <div>
                                        <span className="text-slate-700 dark:text-slate-300 text-sm font-bold leading-normal mb-3 block">Check Size Range</span>
                                        <div className="flex items-center gap-3">
                                            <div className="w-24 shrink-0">
                                                <select
                                                    value={currency}
                                                    onChange={(e) => setCurrency(e.target.value)}
                                                    className="w-full h-11 rounded-lg border border-slate-200 dark:border-slate-600 bg-white/50 dark:bg-slate-900/50 dark:text-white px-3 text-sm font-bold focus:border-[#ec4899] focus:ring-0 outline-none cursor-pointer"
                                                >
                                                    <option value="INR">INR</option>
                                                    <option value="USD">USD</option>
                                                </select>
                                            </div>
                                            <input
                                                value={minCheckSize}
                                                onChange={(e) => handleCheckSizeChange(e, setMinCheckSize)}
                                                className="flex-1 w-full h-11 rounded-lg border border-slate-200 dark:border-slate-600 bg-white/50 dark:bg-slate-900/50 dark:text-white px-4 text-base focus:border-[#ec4899] focus:ring-0 outline-none transition-colors"
                                                placeholder="Min"
                                                type="text"
                                            />
                                            <span className="text-slate-400 font-bold">-</span>
                                            <input
                                                value={maxCheckSize}
                                                onChange={(e) => handleCheckSizeChange(e, setMaxCheckSize)}
                                                className="flex-1 w-full h-11 rounded-lg border border-slate-200 dark:border-slate-600 bg-white/50 dark:bg-slate-900/50 dark:text-white px-4 text-base focus:border-[#ec4899] focus:ring-0 outline-none transition-colors"
                                                placeholder="Max"
                                                type="text"
                                            />
                                        </div>
                                    </div>

                                    <div>
                                        <label className="block">
                                            <span className="text-slate-700 dark:text-slate-300 text-sm font-bold leading-normal mb-3 block">Preferred Geography</span>
                                            <div className="relative">
                                                <select
                                                    value={preferredGeo}
                                                    onChange={(e) => setPreferredGeo(e.target.value)}
                                                    className="w-full h-11 rounded-lg border border-slate-200 dark:border-slate-600 bg-white/50 dark:bg-slate-900/50 dark:text-white px-4 text-base font-medium focus:border-[#ec4899] focus:ring-0 outline-none appearance-none cursor-pointer">
                                                    <option value="">Select Region</option>
                                                    <option value="India">India</option>
                                                    <option value="US">US</option>
                                                    <option value="All">All</option>
                                                </select>
                                                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-slate-500">
                                                    <span className="material-symbols-outlined">expand_more</span>
                                                </div>
                                            </div>
                                        </label>
                                    </div>
                                </div>
                            </div>
                        </div>



                    </section>
                </div>

                <div className="mt-12 flex flex-col-reverse sm:flex-row justify-end gap-5 border-t border-indigo-100 dark:border-slate-800 pt-8">
                    <Link to="/register" className="px-8 py-4 rounded-xl text-slate-600 dark:text-slate-300 font-bold hover:bg-indigo-50 dark:hover:bg-slate-800 transition-colors text-center">
                        Back
                    </Link>
                    <button
                        onClick={async () => {
                            try {


                                setIsLoading(true);

                                // 1. Create Firebase User
                                const userCred = await createUserWithEmailAndPassword(auth, email, password);
                                const uid = userCred.user.uid;

                                // 2. Send Email Verification
                                try {
                                    const actionCodeSettings = {
                                        url: 'https://uniscout.ai/dashboard',
                                        handleCodeInApp: true,
                                    };
                                    await sendEmailVerification(userCred.user, actionCodeSettings);
                                } catch (err) {
                                    console.error("Failed to send verification email:", err);
                                }

                                // 3. Upload Avatar (Authenticated)
                                let avatarUrl = null;
                                if (avatarFile) {
                                    try {
                                        const res = await import('@/services/imageService').then(m => m.uploadProfileIcon(avatarFile));
                                        avatarUrl = (res as any).url || (res as any).signed_url || (res as any).gcs_path;
                                    } catch (err) {
                                        console.error("Avatar upload failed:", err);
                                    }
                                }

                                // 4. Register on Backend
                                const finalOrgName = isIndividual ? `${fullName}'s Team` : orgName;

                                const userResponse = await uploadService.registerUser({
                                    id: uid,
                                    email: email,
                                    full_name: fullName,
                                    job_title: title,
                                    role: 'investor',
                                    organization_name: finalOrgName,
                                    linkedin_url: linkedIn,
                                    avatar_url: avatarUrl,
                                    org_logo_url: null,
                                    investor_profile: {
                                        focus_industry: focusIndustry,
                                        investment_stage: investmentStage,
                                        min_check_size: minCheckSize,
                                        max_check_size: maxCheckSize,
                                        currency: currency,
                                        preferred_geography: preferredGeo
                                    }
                                });

                                // 5. Upload Org Logo (Authenticated & Org Aware)
                                if (orgLogoFile && !isIndividual && userResponse?.user?.organizations?.length > 0) {
                                    try {
                                        const org = userResponse.user.organizations[0];
                                        if (org && org.id) {
                                            await import('@/services/imageService').then(m => m.uploadStartupLogo(org.id, orgLogoFile));
                                        }
                                    } catch (err) {
                                        console.error("Logo upload failed:", err);
                                    }
                                }

                                // 6. Redirect to email verification page
                                navigate('/auth/verify-email', { state: { email } });

                            } catch (e: any) {
                                alert(e.message || "Registration failed");
                            } finally {
                                setIsLoading(false);
                            }
                        }}
                        disabled={isLoading}
                        className="px-10 py-4 rounded-xl bg-logo-gradient text-white font-bold shadow-xl shadow-indigo-500/20 hover:shadow-indigo-500/30 transition-all flex items-center justify-center gap-2 transform hover:-translate-y-0.5 disabled:opacity-70 disabled:cursor-not-allowed">
                        {isLoading ? 'Creating Account...' : 'Create Account'}
                        {!isLoading && <span className="material-symbols-outlined text-base">arrow_forward</span>}
                    </button>
                    <div className="hidden">
                        {/* Hidden import to ensure it's bundled if needed, though dynamic import above works */}
                    </div>
                </div>
            </main>
        </div>
    );
}
